import { Inject, Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  Observable, of, combineLatest
} from 'rxjs';
import {
  concatMap, map, scan, startWith, take, tap
} from 'rxjs/operators';

import { extractDocVersion } from 'app/utils/document-version';
import { isBomSubLine } from 'app/utils/is-bom-sub-line';
import {
  AppConfigurationSection as ConfigurationSection,
  AppConfigurationVariable as ConfigurationVariable,
  AppProductLine as ProductLine,
  AppVariableAssignmentPayload as VariableAssignmentPayload,
  AssignmentChange,
  Offer,
  PositionPricingUpdate
} from 'graphql/models/extended';
import { VariableAssignMutation } from 'graphql/mutations/variable-assign';
import { VariableUnassignMutation } from 'graphql/mutations/variable-unassign';
import { OfferBase } from 'models/offer.model';
import { PositionField } from 'models/position.model';
import { OfferPricing } from 'models/pricing.model';
import { OfferPositionService } from 'shared/services/offer-position.service';
import { PricingService } from 'shared/services/pricing.service';

@Injectable({
  providedIn: 'root'
})
export class VariableAssignmentService {
  constructor(
    @Inject('datamodel') private datamodel: Record<string, any>,
    private variableAssignMutation: VariableAssignMutation,
    private variableUnassignMutation: VariableUnassignMutation,
    private pricingService: PricingService,
    private offerPositionService: OfferPositionService
  ) {
    //
  }

  removeAssignment(
    offer: OfferBase,
    position: ProductLine,
    variableId: string,
    formControls?: Record<string, AbstractControl>
  ): Observable<AssignmentChange> {
    return this.changeAssignment(
      offer,
      position,
      variableId,
      undefined,
      formControls
    );
  }

  private prepareChangeAssignmentVariables(
    offer: OfferBase,
    lineId: string,
    variableId: string,
    value?: string,
    withPricing?: boolean,
    withLines?: boolean
  ): Observable<VariableAssignmentPayload> {
    const remove = value == null || value === '';
    const mutation = remove ? this.variableUnassignMutation : this.variableAssignMutation;
    const variables: Record<PropertyKey, unknown> = {
      documentVersion: extractDocVersion(offer),
      lineId,
      variableId: remove ? String(variableId) : undefined,
      variableAssignment: remove ? undefined : {
        variableId: String(variableId),
        value: String(value)
      },
      withPricing,
      withLines
    };

    return mutation.mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe(map<any, VariableAssignmentPayload>(
        (r) => r?.data?.quotes?.modify?.line?.configuration
          ?.[remove ? 'unassign' : 'assign']
      ), take(1));
  }

  changeAssignment(
    offer: OfferBase,
    position: ProductLine,
    variableId: string,
    value?: string,
    formControls?: Record<string, AbstractControl>
  ): Observable<AssignmentChange> {
    return this.prepareChangeAssignmentVariables(offer, position.id, variableId, value)
      .pipe(
        map((changes) => this.mergeAssignmentChanges(position, changes, formControls)),
        concatMap(({ removedVariables, updatedPosition }: AssignmentChange): Observable<AssignmentChange> => {
          const changedPositions = updatedPosition
            ? [updatedPosition]
            : [];

          return of({ changedPositions, removedVariables });
        }),
        concatMap((res) => combineLatest([
          this.pricingService.ensurePricesUpdated(offer, position.id).pipe(
            map((o) => ({ pricing: (o && o.line && o.line.pricing) || undefined }))
          )
        ]).pipe(
          startWith([res]),
          scan((acc, curr) => ({ ...acc, ...curr[0] }), res)
        ))
      );
  }

  private mergeAssignmentChanges(
    position: ProductLine,
    changes: VariableAssignmentPayload,
    formControls?: Record<string, AbstractControl>
  ): AssignmentChange {
    const updatedPosition = { ...position };
    const removedVariables: Pick<ConfigurationVariable, 'id' | 'name'>[] = [];

    if (!changes || !changes.line) {
      return { updatedPosition, removedVariables } as AssignmentChange;
    }

    const changedPosition: ProductLine = this.offerPositionService.mapPosition(changes.line) as ProductLine;
    if (!changedPosition.configuration) {
      return { updatedPosition, removedVariables } as AssignmentChange;
    }

    updatedPosition.configuration = { ...changedPosition.configuration };

    // update product name
    if (changedPosition.snkProductName) {
      updatedPosition.snkProductName = changedPosition.snkProductName;
      if (updatedPosition.product) {
        updatedPosition.product.productName = changedPosition.snkProductName;
      }
    }

    const changedSections = (changedPosition.configuration.sections || [])
      .filter((section) => !['Einstieg'].includes(section.id)); // TODO: remove after DMs are fixed

    // removed logic
    const oldSectionIds: string[] = [];

    ((position.configuration?.sections) || []).forEach((section) => {
      oldSectionIds.push(section.id);

      // find removed variables
      this.mapRemovedAssignmentsVariables(
        removedVariables,
        section as ConfigurationSection,
        changedSections as ConfigurationSection[],
        formControls
      );
    });

    // changed and new logic
    const dimensions = (Object.values(this.datamodel.dimensions) as string[]).flat().filter(Boolean);
    updatedPosition.configuration.sections = changedSections.map((section) => {
      let isChangedSection = false;
      const isNewSection = !oldSectionIds.includes(section.id);

      const items = ((section.variables.items || []) as ConfigurationVariable[]).map((item) => {
        let isChangedVar = false;
        let isNewVar = false;

        if (formControls && formControls[item.id]) {
          isChangedVar = (formControls[item.id].value !== item.value);
        } else {
          isNewVar = true;
        }

        if (!(dimensions.includes(item.id)) && (isChangedVar || isNewVar) && !isChangedSection && !isNewSection) {
          isChangedSection = true;
        }

        return {
          ...item,
          isChanged: isChangedVar,
          isNew: isNewVar
        } as ConfigurationVariable;
      });

      return {
        ...section,
        isChanged: isChangedSection,
        isNew: isNewSection,
        variables: { items }
      } as ConfigurationSection;
    });

    if (changedPosition.snkDimensions) {
      updatedPosition.snkDimensions = {
        ...updatedPosition.snkDimensions,
        ...changedPosition.snkDimensions
      };
    }

    if (changedPosition.pricing) {
      updatedPosition.pricing = {
        ...updatedPosition.pricing,
        ...changedPosition.pricing
      };
    }

    return { updatedPosition, removedVariables } as AssignmentChange;
  }

  private mapRemovedAssignmentsVariables(
    removedVars: Pick<ConfigurationVariable, 'id' | 'name'>[],
    section: ConfigurationSection,
    changedSections: ConfigurationSection[],
    formControls?: Record<string, AbstractControl>
  ): void {
    const changedSection = changedSections.find((s) => s.id === section.id);
    const sectionVars = ((section.variables.items || []) as ConfigurationVariable[]);

    sectionVars.forEach((v) => {
      if (formControls && formControls[v.id]) {
        if (!changedSection) {
          removedVars.push({ id: v.id, name: v.name });
        } else {
          const findVar = (changedSection.variables.items || []).find(
            (i) => i.id === v.id
          );
          if (!findVar) {
            removedVars.push({ id: v.id, name: v.name });
          }
        }
      }
    });
  }

  updatePositionMeasurements(
    offer: Offer,
    positionId: string,
    variableId = '',
    value?: string,
    updateOffer?: PositionField,
    updatePricing?: boolean
  ): Observable<Offer> {
    const position = offer.productPositions.flatMap((productPosition) => productPosition.positions)
      .find((pos) => pos.id === positionId);

    if (!position) {
      return of(offer);
    }

    const withLines = true; // (position.subLines !== undefined && position.subLines.length > 0);
    const withPricing = true;  // updateOffer !== PositionField.DIRECTION

    const changeMeasurement$ = this.prepareChangeAssignmentVariables(
      offer,
      position.id,
      variableId,
      value,
      withPricing,
      withLines
    );

    return updateOffer
      ? changeMeasurement$.pipe(
        map((res) => {
          const { pricing, snkDimensions } = res.line || {};
          const lines = (withLines ? res.quote.lines as ProductLine[] : []);
          const bomLines = lines.filter((pos) => isBomSubLine(pos) && pos.parentLine && pos.parentLine.id === positionId);
          const positionPricingUpdate: PositionPricingUpdate = {};

          if (pricing) {
            positionPricingUpdate[positionId] = pricing;
          }
          bomLines.map((pos) => {
            if (pos.pricing) {
              positionPricingUpdate[pos.id] = pos.pricing;
            }
          });
          const offerPricingUpdate = res.quote && res.quote.pricing ? res.quote.pricing : undefined;
          const autoUpdatePrices = res.quote.autoUpdatePrices;
          const priceIsOutdated = res.quote.priceIsOutdated;

          const match = offer.productPositions
            .flatMap(pp => pp.positions)
            .find(pos => pos.id === res.line.id);

            if (match) {
              (match as any).snkDimensions = snkDimensions;
            }

          let updateOffer: Offer = {
            ...offer,
            line: res.line,
            lines,
            positionPricingUpdate,
            autoUpdatePrices,
            priceIsOutdated
          };

          if (offerPricingUpdate) {
            updateOffer = { ...updateOffer, offerPricingUpdate: offerPricingUpdate as OfferPricing }
          }

          return updateOffer;
        }),
        tap((offer) => {
          if (!updatePricing) {
            this.pricingService.setOfferPricingStateInStore(offer);
          }
        })
      )
      : changeMeasurement$.pipe(
        map((res) => ({
          ...offer,
          line: res.line
        })),
        tap((offer) => {
          if (!updatePricing) {
            this.pricingService.setOfferPricingStateInStore(offer);
          }
        })
      );
  }
}
